//src/components/Teams.js
import React, { useState, useEffect, useRef } from "react";
import { auth, db } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  deleteDoc,
  updateDoc,
  Timestamp,
  collection as firestoreCollection,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import Header from "./Header";
import { getFunctions, httpsCallable } from "firebase/functions";
import TaskDetailsModal from "./TaskDetailsModal";

// Hàm chuyển URL thành link có thể click
const linkifyText = (text) => {
  if (!text) return null;
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
  const result = [];
  let lastIndex = 0;
  let match;
  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push(text.substring(lastIndex, match.index));
    }
    let url = match[0];
    const href = url.startsWith('http') ? url : `http://${url}`;
    result.push(
      <a key={`link-${match.index}`} href={href} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">{url}</a>
    );
    lastIndex = match.index + url.length;
  }
  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex));
  }
  return result;
};

const Teams = () => {
  const navigate = useNavigate();
  const functions = getFunctions();
  const [teams, setTeams] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");
  const [newTeamDescription, setNewTeamDescription] = useState("");
  const [newTeamPrivate, setNewTeamPrivate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showSidebar, setShowSidebar] = useState(false);
  const [userData, setUserData] = useState(null);
  const [memberEmail, setMemberEmail] = useState("");
  const [memberRole, setMemberRole] = useState("team_member");
  const [teamMembers, setTeamMembers] = useState([]);
  const [emailError, setEmailError] = useState("");
  const [searchingUser, setSearchingUser] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showTeamDetailModal, setShowTeamDetailModal] = useState(false);
  const [confirmDisbandModal, setConfirmDisbandModal] = useState(false);
  const [teamDisbanding, setTeamDisbanding] = useState(false);
  const [newMemberEmail, setNewMemberEmail] = useState("");
  const [newMemberRole, setNewMemberRole] = useState("team_member");
  const [newMemberSearchResults, setNewMemberSearchResults] = useState([]);
  const [showNewMemberSearchResults, setShowNewMemberSearchResults] =
    useState(false);
  const [addingNewMember, setAddingNewMember] = useState(false);
  const [newMemberError, setNewMemberError] = useState("");
  const [removingMemberId, setRemovingMemberId] = useState(null);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [memberUpdating, setMemberUpdating] = useState(false);
  const sidebarRef = useRef(null);
  const [teamPermissions, setTeamPermissions] = useState({});
  const [systemManagers, setSystemManagers] = useState([]);
  const [systemManagerSearch, setSystemManagerSearch] = useState("");
  const [systemManagerSearchResults, setSystemManagerSearchResults] = useState([]);
  const [systemManagerLoading, setSystemManagerLoading] = useState(false);
  const [systemManagerError, setSystemManagerError] = useState("");
  const [addingSystemManager, setAddingSystemManager] = useState(false);
  // State cho chỉnh sửa mô tả
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedDescription, setEditedDescription] = useState("");
  // State cho thông báo toast
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState(false);
  const [selectedTaskForDetail, setSelectedTaskForDetail] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userRef = doc(db, "users", user.uid);
          const userSnap = await getDoc(userRef);

          if (userSnap.exists()) {
            setUserData({
              uid: user.uid,
              email: user.email,
              displayName:
                userSnap.data().displayName || user.displayName || "Người dùng",
              photoURL:
                user.photoURL ||
                "https://cdn-icons-png.flaticon.com/512/6897/6897018.png",
              ...userSnap.data(),
            });
          } else {
            setUserData({
              uid: user.uid,
              email: user.email,
              displayName: user.displayName || "Người dùng",
              photoURL:
                user.photoURL ||
                "https://cdn-icons-png.flaticon.com/512/6897/6897018.png",
            });
          }
          fetchUserTeams(user.uid); // Truyền userId rõ ràng
        } catch (error) {
          console.error("Lỗi khi tải dữ liệu người dùng:", error);
        }
      } else {
        setUserData(null);
        setTeams([]); // Không hiển thị nhóm nào nếu chưa đăng nhập
      }
    });
    return () => unsubscribe();
  }, []);

  const fetchUserTeams = async (userIdParam) => {
    try {
      const userId = userIdParam || auth.currentUser?.uid;
      if (!userId) {
        setTeams([]);
        setLoading(false);
        return;
      }
      // Nếu là system admin, load tất cả teams
      const email = auth.currentUser?.email || '';
      const systemAdmins = ['ha.ttt@greenschool.edu.vn', 'maxdevfs@gmail.com', 'xofosy@thetechnext.net'];
      if (systemAdmins.includes(email)) {
        // Lấy tất cả phòng ban từ collection teams
        const teamsCol = firestoreCollection(db, 'teams');
        const snap = await getDocs(teamsCol);
        
        // Lọc team không private hoặc không có trường private (trường hợp team cũ)
        // HOẶC nếu admin hệ thống là member của team đó
        const allTeams = [];
        snap.forEach(d => {
          const data = d.data();
          const isPublic = data.private === undefined || data.private === false;
          const isUserMember = data.members?.includes(userId) || false;
          const isVisible = (isPublic || isUserMember) &&
                           // Đảm bảo team có members hoặc membersData (loại bỏ team rỗng)
                           (Array.isArray(data.members) || Array.isArray(data.membersData) || 
                           (typeof data.members === 'object' && data.members !== null));
          
          if (isVisible) {
            allTeams.push({ id: d.id, ...data });
          }
        });
        
        console.log("Admin view - Số team hiển thị:", allTeams.length);
        setTeams(allTeams);
        setLoading(false);
        return;
      }
      
      // Sửa lại tham số truyền vào cloud function getUserTeams để khớp với tham số mà function này mong đợi
      setLoading(true);
      const getUserTeams = httpsCallable(functions, 'getUserTeams');
      console.log(`Gọi getUserTeams với userId: ${userId}`);
      // Truyền tham số targetUserId thay vì userId
      const result = await getUserTeams({ targetUserId: userId });
      console.log('Kết quả từ getUserTeams:', result.data);
      
      // Lấy danh sách team từ kết quả trả về
      // FIX BUG: Hiển thị tất cả teams mà user là member (bao gồm cả private teams)
      // vì cloud function getUserTeams đã đảm bảo chỉ trả về teams mà user có quyền xem
      const teamsData = result.data.teams || [];
      
      setTeams(teamsData);
      setLoading(false);
    } catch (error) {
      console.error("Lỗi khi tải danh sách phòng ban:", error);
      setTeams([]);
      setLoading(false);
    }
  };

  const fetchSystemManagers = async () => {
    try {
      setSystemManagerLoading(true);
      const colRef = firestoreCollection(db, "system_managers");
      const snapshot = await getDocs(colRef);
      const managers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setSystemManagers(managers);
    } catch (err) {
      setSystemManagerError("Không thể tải danh sách quản lý hệ thống");
    } finally {
      setSystemManagerLoading(false);
    }
  };

  useEffect(() => {
    if (userData?.email === "ha.ttt@greenschool.edu.vn" || userData?.email === "maxdevfs@gmail.com") {
      fetchSystemManagers();
    }
  }, [userData]);

  const searchUsers = async (searchText, forNewMember = false) => {
    if (!searchText.trim() || searchText.length < 2) {
      if (forNewMember) {
        setNewMemberSearchResults([]);
        setShowNewMemberSearchResults(false);
      } else {
        setSearchResults([]);
        setShowSearchResults(false);
      }
      return;
    }

    try {
      const searchUsersFunction = httpsCallable(functions, "searchUsers");
      const result = await searchUsersFunction({ searchText });

      const users = result.data.results || [];

      if (forNewMember) {
        setNewMemberSearchResults(users);
        setShowNewMemberSearchResults(users.length > 0);
      } else {
        setSearchResults(users);
        setShowSearchResults(users.length > 0);
      }
    } catch (error) {
      console.error("Lỗi khi tìm kiếm người dùng:", error);
    }
  };

  const handleSystemManagerSearch = async (searchText) => {
    setSystemManagerSearch(searchText);
    if (!searchText.trim() || searchText.length < 2) {
      setSystemManagerSearchResults([]);
      return;
    }
    try {
      setSystemManagerLoading(true);
      const searchUsersFunction = httpsCallable(functions, "searchUsers");
      const result = await searchUsersFunction({ searchText });
      setSystemManagerSearchResults(result.data.results || []);
    } catch (err) {
      setSystemManagerError("Lỗi khi tìm kiếm người dùng");
    } finally {
      setSystemManagerLoading(false);
    }
  };

  const handleAddSystemManager = async (user) => {
    if (!user?.uid) return;
    setAddingSystemManager(true);
    setSystemManagerError("");
    try {
      // Cho phép admin lớn nhất tự add chính mình vào System Managers (UI)
      if (
        user?.uid === auth.currentUser?.uid &&
        (userData?.email === "ha.ttt@greenschool.edu.vn" || userData?.email === "maxdevfs@gmail.com")
      ) {
        // Không chặn tự add chính mình nếu là admin lớn nhất
      } else if (systemManagers.some(m => m.uid === user.uid)) {
        setSystemManagerError("Người này đã là quản lý hệ thống");
        setAddingSystemManager(false);
        return;
      }
      await addDoc(firestoreCollection(db, "system_managers"), {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName || "Người dùng",
        photoURL: user.photoURL || "https://cdn-icons-png.flaticon.com/512/6897/6897018.png",
        addedAt: serverTimestamp(),
      });
      setSystemManagerSearch("");
      setSystemManagerSearchResults([]);
      fetchSystemManagers();
    } catch (err) {
      setSystemManagerError("Không thể thêm quản lý hệ thống");
    } finally {
      setAddingSystemManager(false);
    }
  };

  const handleRemoveSystemManager = async (managerId) => {
    if (!managerId) return;
    setSystemManagerError("");
    try {
      await deleteDoc(doc(db, "system_managers", managerId));
      fetchSystemManagers();
    } catch (err) {
      setSystemManagerError("Không thể xoá quản lý hệ thống");
    }
  };

  useEffect(() => {
    // Debounce tìm kiếm khi tạo phòng ban
    const timeoutId = setTimeout(() => {
      searchUsers(memberEmail);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [memberEmail]);

  useEffect(() => {
    // Debounce tìm kiếm khi thêm thành viên mới trong chi tiết phòng ban
    const timeoutId = setTimeout(() => {
      searchUsers(newMemberEmail, true);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [newMemberEmail]);

  const handleAddMember = async (selectedUser = null) => {
    let userToAdd;
    const currentUserId = auth.currentUser?.uid;
    const currentUserEmail = userData?.email;

    if (selectedUser) {
      userToAdd = {
        ...selectedUser,
        role: memberRole,
      };
    } else if (!memberEmail.trim()) {
      setEmailError("Vui lòng nhập địa chỉ email");
      return;
    } else {
      if (
        memberEmail.trim().toLowerCase() === currentUserEmail?.toLowerCase()
      ) {
        setEmailError("Bạn không thể tự thêm mình làm thành viên phòng ban");
        return;
      }

      setSearchingUser(true);
      setEmailError("");

      try {
        const usersRef = collection(db, "users");
        const q = query(usersRef, where("email", "==", memberEmail.trim()));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          setEmailError("Không tìm thấy người dùng với địa chỉ email này");
          setSearchingUser(false);
          return;
        }

        const userDoc = querySnapshot.docs[0];

        if (userDoc.id === currentUserId) {
          setEmailError("Bạn không thể tự thêm mình làm thành viên phòng ban");
          setSearchingUser(false);
          return;
        }

        userToAdd = {
          uid: userDoc.id,
          email: userDoc.data().email,
          displayName: userDoc.data().displayName || "Người dùng",
          role: memberRole,
        };
      } catch (error) {
        console.error("Lỗi khi kiểm tra người dùng:", error);
        setEmailError("Lỗi khi kiểm tra người dùng");
        setSearchingUser(false);
        return;
      } finally {
        setSearchingUser(false);
      }
    }

    const exists = teamMembers.some(
      (member) => member.email === userToAdd.email
    );
    if (exists) {
      setEmailError("Người dùng này đã được thêm vào phòng ban");
      return;
    }

    setTeamMembers([...teamMembers, userToAdd]);
    setMemberEmail("");
    setMemberRole("team_member");
    setShowSearchResults(false);
  };

  const handleAddNewMember = async (selectedUser = null) => {
    if (!selectedTeam) return;

    let userToAdd;
    const currentUserId = auth.currentUser?.uid;
    const currentUserEmail = userData?.email;

    if (selectedUser) {
      userToAdd = {
        ...selectedUser,
        role: newMemberRole,
      };
    } else if (!newMemberEmail.trim()) {
      setNewMemberError("Vui lòng nhập địa chỉ email");
      return;
    } else {
      if (
        newMemberEmail.trim().toLowerCase() === currentUserEmail?.toLowerCase()
      ) {
        setNewMemberError(
          "Bạn không thể tự thêm mình làm thành viên phòng ban"
        );
        return;
      }

      setAddingNewMember(true);
      setNewMemberError("");

      try {
        const searchUsersFunction = httpsCallable(functions, "searchUsers");
        const result = await searchUsersFunction({
          searchText: newMemberEmail.trim(),
        });

        const users = result.data.results || [];

        if (users.length === 0) {
          setNewMemberError("Không tìm thấy người dùng với địa chỉ email này");
          setAddingNewMember(false);
          return;
        }

        userToAdd = {
          ...users[0],
          role: newMemberRole,
        };

        if (userToAdd.uid === currentUserId) {
          setNewMemberError("Bạn không thể tự thêm mình lần nữa");
          setAddingNewMember(false);
          return;
        }
      } catch (error) {
        console.error("Lỗi khi tìm kiếm người dùng:", error);
        setNewMemberError("Lỗi khi tìm kiếm người dùng");
        setAddingNewMember(false);
        return;
      }
    }

    try {
      const addTeamMemberFunction = httpsCallable(functions, "addTeamMember");
      const result = await addTeamMemberFunction({
        teamId: selectedTeam.id,
        memberData: userToAdd,
      });

      const updatedMembersData = result.data.updatedMembers;
      const updatedMembers = updatedMembersData.map((member) => member.uid);

      setSelectedTeam({
        ...selectedTeam,
        members: updatedMembers,
        membersData: updatedMembersData,
      });

      setTeams(
        teams.map((team) =>
          team.id === selectedTeam.id
            ? {
                ...team,
                members: updatedMembers,
                membersData: updatedMembersData,
              }
            : team
        )
      );

      setNewMemberEmail("");
      setNewMemberRole("team_member");
      setShowNewMemberSearchResults(false);
    } catch (error) {
      console.error("Lỗi khi thêm thành viên mới:", error);
      let errorMessage = "Không thể thêm thành viên vào phòng ban";

      if (error.details) {
        if (error.details.code === "already-exists") {
          errorMessage = "Người dùng này đã là thành viên của phòng ban";
        } else if (error.details.code === "permission-denied") {
          errorMessage = "Bạn không có quyền thêm thành viên vào phòng ban này";
        }
      }

      setNewMemberError(errorMessage);
    } finally {
      setAddingNewMember(false);
    }
  };

  const changeMemberRole = (email, newRole) => {
    setTeamMembers(
      teamMembers.map((member) =>
        member.email === email ? { ...member, role: newRole } : member
      )
    );
  };

  const updateTeamMemberRole = async (memberId, newRole) => {
    if (!selectedTeam) return;

    setMemberUpdating(true);
    try {
      const updateTeamMemberRoleFunction = httpsCallable(
        functions,
        "updateTeamMemberRole"
      );
      const result = await updateTeamMemberRoleFunction({
        teamId: selectedTeam.id,
        memberId: memberId,
        newRole: newRole,
      });

      const updatedMembersData = result.data.updatedMembers;

      setSelectedTeam({
        ...selectedTeam,
        membersData: updatedMembersData,
      });

      setTeams(
        teams.map((team) =>
          team.id === selectedTeam.id
            ? { ...team, membersData: updatedMembersData }
            : team
        )
      );
    } catch (error) {
      console.error("Lỗi khi cập nhật vai trò thành viên:", error);
    } finally {
      setMemberUpdating(false);
    }
  };

  const selectUserFromSearch = (user) => {
    setMemberEmail(user.email);
    setShowSearchResults(false);
  };

  const selectNewMemberFromSearch = (user) => {
    setNewMemberEmail(user.email);
    setShowNewMemberSearchResults(false);
  };

  const removeMember = (email) => {
    setTeamMembers(teamMembers.filter((member) => member.email !== email));
  };

  const confirmRemoveMember = (memberId) => {
    setRemovingMemberId(memberId);
    setShowRemoveConfirmation(true);
  };

  const removeTeamMember = async () => {
    if (!selectedTeam || !removingMemberId) return;

    try {
      const removeTeamMemberFunction = httpsCallable(
        functions,
        "removeTeamMember"
      );
      const result = await removeTeamMemberFunction({
        teamId: selectedTeam.id,
        memberId: removingMemberId,
      });

      const updatedMembersData = result.data.updatedMembers;
      const updatedMembers = updatedMembersData.map((member) => member.uid);

      setSelectedTeam({
        ...selectedTeam,
        members: updatedMembers,
        membersData: updatedMembersData,
      });

      setTeams(
        teams.map((team) =>
          team.id === selectedTeam.id
            ? {
                ...team,
                members: updatedMembers,
                membersData: updatedMembersData,
              }
            : team
        )
      );

      setShowRemoveConfirmation(false);
      setRemovingMemberId(null);
    } catch (error) {
      console.error("Lỗi khi xóa thành viên phòng ban:", error);
    }
  };

  const viewTeamDetails = (team) => {
    setSelectedTeam(team);
    setShowTeamDetailModal(true);
  };

  const goToTeamTasks = (teamId) => {
    navigate(`/team/${teamId}/tasks`);
  };

  const closeTeamDetails = () => {
    setShowTeamDetailModal(false);
    setSelectedTeam(null);
    setNewMemberEmail("");
    setNewMemberRole("team_member");
    setNewMemberError("");
  };

  const openDisbandConfirmation = () => {
    setConfirmDisbandModal(true);
  };

  const closeDisbandConfirmation = () => {
    setConfirmDisbandModal(false);
  };

  const closeRemoveConfirmation = () => {
    setShowRemoveConfirmation(false);
    setRemovingMemberId(null);
  };

  const disbandTeam = async () => {
    if (!selectedTeam) return;

    setTeamDisbanding(true);
    try {
      const disbandTeamFunction = httpsCallable(functions, "disbandTeam");
      await disbandTeamFunction({ teamId: selectedTeam.id });

      setTeams(teams.filter((team) => team.id !== selectedTeam.id));
      setShowTeamDetailModal(false);
      setConfirmDisbandModal(false);
      setSelectedTeam(null);
    } catch (error) {
      console.error("Lỗi khi giải tán phòng ban:", error);
    } finally {
      setTeamDisbanding(false);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    try {
      if (!newTeamName.trim()) return;

      const createTeamFunction = httpsCallable(functions, "createTeam");
      await createTeamFunction({
        name: newTeamName.trim(),
        members: teamMembers,
        private: newTeamPrivate,
        description: newTeamDescription.trim(),
      });

      setNewTeamName("");
      setNewTeamDescription("");
      setNewTeamPrivate(false);
      setTeamMembers([]);
      setShowCreateModal(false);
      fetchUserTeams();
    } catch (error) {
      console.error("Lỗi khi tạo phòng ban:", error);
      alert("Không thể tạo phòng ban. Vui lòng thử lại.");
    }
  };

  const handleOutsideClick = (e) => {
    if (
      sidebarRef.current &&
      !sidebarRef.current.contains(e.target) &&
      e.target.classList.contains("sidebar-overlay")
    ) {
      setShowSidebar(false);
    }
  };

  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === "Escape" && showSidebar) {
        setShowSidebar(false);
      }
    };

    if (showSidebar) {
      document.addEventListener("keydown", handleEscKey);
    }

    return () => {
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [showSidebar]);

  const isUserTeamAdmin = async (team) => {
    if (!team || !auth.currentUser) return false;

    try {
      const checkTeamPermissionFunction = httpsCallable(
        functions,
        "checkIsTeamAdmin"
      );
      const result = await checkTeamPermissionFunction({ teamId: team.id });
      return result.data.isAdmin;
    } catch (error) {
      console.error("Lỗi khi kiểm tra quyền quản trị viên phòng ban:", error);
      return false;
    }
  };

  const isUserTeamManager = async (team) => {
    if (!team || !auth.currentUser) return false;

    try {
      const checkTeamPermissionFunction = httpsCallable(
        functions,
        "checkIsTeamManager"
      );
      const result = await checkTeamPermissionFunction({ teamId: team.id });
      return result.data.isManager;
    } catch (error) {
      console.error("Lỗi khi kiểm tra quyền quản lý phòng ban:", error);
      return false;
    }
  };

  useEffect(() => {
    if (selectedTeam) {
      const checkPermissions = async () => {
        const isAdmin = await isUserTeamAdmin(selectedTeam);
        const isManager = await isUserTeamManager(selectedTeam);

        setTeamPermissions({
          isAdmin,
          isManager,
          teamId: selectedTeam.id,
        });
      };

      checkPermissions();
    }
  }, [selectedTeam]);

  const checkIsAdmin = (team) => {
    // Kiểm tra nếu người dùng là admin hệ thống (bằng email)
    const systemAdmins = ['ha.ttt@greenschool.edu.vn', 'maxdevfs@gmail.com', 'xofosy@thetechnext.net'];
    if (systemAdmins.includes(auth.currentUser?.email)) {
      // Admin hệ thống được coi là admin của tất cả team công khai
      if (team && (team.private === undefined || team.private === false)) {
        return true;
      }
    }

    // Nếu không phải admin hệ thống hoặc team là riêng tư, kiểm tra quyền thông thường
    if (teamPermissions.teamId === team?.id) {
      return teamPermissions.isAdmin;
    }
    return team?.membersData?.some(
      (member) =>
        member.uid === auth.currentUser?.uid && member.role === "admin"
    );
  };

  const checkIsManager = (team) => {
    // Kiểm tra nếu người dùng là admin hệ thống (bằng email)
    const systemAdmins = ['ha.ttt@greenschool.edu.vn', 'maxdevfs@gmail.com', 'xofosy@thetechnext.net'];
    if (systemAdmins.includes(auth.currentUser?.email)) {
      // Admin hệ thống được coi là manager của tất cả team công khai
      if (team && (team.private === undefined || team.private === false)) {
        return true;
      }
    }

    // Nếu không phải admin hệ thống hoặc team là riêng tư, kiểm tra quyền thông thường
    if (teamPermissions.teamId === team?.id) {
      return teamPermissions.isManager;
    }
    return team?.membersData?.some(
      (member) =>
        member.uid === auth.currentUser?.uid &&
        (member.role === "admin" || member.role === "manager")
    );
  };

  // Hiệu ứng tự động ẩn toast
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => setToast(t => ({ ...t, show: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  const handleCloseTaskDetailsModal = () => {
    setShowTaskDetailsModal(false);
    setSelectedTaskForDetail(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-16 dark:bg-gray-900">
      <Header />
      {(userData?.email === "ha.ttt@greenschool.edu.vn" || userData?.email === "maxdevfs@gmail.com") && (
        <div className="max-w-7xl mx-auto mt-6 mb-8 p-6 bg-white dark:bg-gray-800 rounded-lg shadow">
          <h2 className="text-lg font-bold mb-4 text-emerald-700 dark:text-emerald-300">Quản lý hệ thống (System Managers)</h2>
          <div className="mb-4 flex flex-col md:flex-row md:items-center gap-2">
            <input
              type="text"
              value={systemManagerSearch}
              onChange={e => handleSystemManagerSearch(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-700 dark:text-white w-full md:w-80"
              placeholder="Tìm kiếm theo tên hoặc email"
            />
            {systemManagerLoading && <span className="ml-2 text-gray-500 text-sm">Đang tìm...</span>}
          </div>
          {systemManagerSearchResults.length > 0 && (
            <div className="mb-4 bg-gray-50 dark:bg-gray-700 rounded-lg p-2">
              {systemManagerSearchResults.map(user => (
                <div key={user.uid} className="flex items-center justify-between py-1 px-2 hover:bg-gray-100 dark:hover:bg-gray-600 rounded">
                  <div className="flex items-center">
                    <img src={user.photoURL || "https://cdn-icons-png.flaticon.com/512/6897/6897018.png"} alt={user.displayName} className="h-8 w-8 rounded-full mr-2" />
                    <span className="font-medium text-gray-800 dark:text-white">{user.displayName}</span>
                    <span className="ml-2 text-xs text-gray-500">{user.email}</span>
                  </div>
                  <button
                    className="px-3 py-1 bg-emerald-500 text-white rounded hover:bg-emerald-600 text-xs"
                    onClick={() => handleAddSystemManager(user)}
                    disabled={addingSystemManager}
                  >
                    {addingSystemManager ? <i className="fas fa-spinner fa-spin"></i> : "Thêm quản lý hệ thống"}
                  </button>
                </div>
              ))}
            </div>
          )}
          <h3 className="font-semibold mb-2 mt-4 text-gray-700 dark:text-gray-200">Danh sách quản lý hệ thống hiện tại:</h3>
          {systemManagerLoading ? (
            <div>Đang tải...</div>
          ) : (
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {systemManagers.map(manager => (
                <li key={manager.id} className="flex items-center justify-between py-2">
                  <div className="flex items-center">
                    {manager.photoURL ? (
                      <img
                        src={manager.photoURL}
                        alt={manager.displayName}
                        className="h-8 w-8 rounded-full mr-2 object-cover bg-gray-200"
                        onError={e => {
                          e.target.onerror = null;
                          e.target.src = "https://cdn-icons-png.flaticon.com/512/6897/6897018.png";
                        }}
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-emerald-500 flex items-center justify-center mr-2">
                        <span className="text-white font-bold text-base">
                          {manager.displayName?.charAt(0).toUpperCase() || 'U'}
                        </span>
                      </div>
                    )}
                    <span className="font-medium text-gray-800 dark:text-white">{manager.displayName}</span>
                    <span className="ml-2 text-xs text-gray-500">{manager.email}</span>
                  </div>
                  <button
                    className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 text-xs"
                    onClick={() => handleRemoveSystemManager(manager.id)}
                  >
                    Xoá
                  </button>
                </li>
              ))}
              {systemManagers.length === 0 && <li className="text-gray-500 text-sm">Chưa có quản lý hệ thống nào.</li>}
            </ul>
          )}
          {systemManagerError && <div className="text-red-500 mt-2 text-sm">{systemManagerError}</div>}
        </div>
      )}
      <div className="p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Phòng ban của tôi
            </h1>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors dark:bg-emerald-600 dark:hover:bg-emerald-700"
            >
              Tạo phòng ban
            </button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-md p-6 animate-pulse dark:bg-gray-800"
                >
                  <div className="h-6 bg-gray-200 rounded w-3/4 mb-4 dark:bg-gray-700"></div>
                  <div className="h-4 bg-gray-200 rounded w-full mb-2 dark:bg-gray-700"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6 dark:bg-gray-700"></div>
                  <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
                    <div className="h-8 bg-gray-200 rounded w-1/3 dark:bg-gray-700"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : teams.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center dark:bg-gray-800">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-emerald-900/30">
                <i className="fas fa-users text-emerald-500 text-2xl dark:text-emerald-400"></i>
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2 dark:text-white">
                Chưa có phòng ban nào
              </h2>
              <p className="text-gray-600 mb-6 dark:text-gray-300">
                Tạo phòng ban đầu tiên của bạn để bắt đầu cộng tác với những
                người khác.
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors dark:bg-emerald-600 dark:hover:bg-emerald-700"
              >
                Tạo phòng ban
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {teams.map((team) => (
                <div
                  key={team.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden dark:bg-gray-800"
                >
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-xl font-semibold text-gray-800 dark:text-white">
                        {team.name}
                      </h2>
                      {team.private && (
                        <span className="text-gray-500 dark:text-gray-400" title="Phòng ban riêng tư">
                          <i className="fas fa-lock"></i>
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 mb-4 dark:text-gray-300 whitespace-pre-line">
                      {team.description ? linkifyText(team.description) : null}
                    </p>
                    <div className="flex items-center text-sm text-gray-500 mb-4 dark:text-gray-400">
                      <i className="fas fa-users mr-2"></i>
                      <span>{team.membersData?.length || 0} thành viên</span>
                      {team.private && (
                        <span className="ml-3 flex items-center text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
                          <i className="fas fa-lock text-xs mr-1"></i>
                          Riêng tư
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between dark:bg-gray-700 dark:border-gray-600">
                    <button
                      onClick={() => viewTeamDetails(team)}
                      className="text-emerald-600 hover:text-emerald-700 font-medium dark:text-emerald-400 dark:hover:text-emerald-300"
                    >
                      Chi tiết phòng ban
                    </button>
                    <button
                      onClick={() => goToTeamTasks(team.id)}
                      className="text-blue-600 hover:text-blue-700 font-medium dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      Xem công việc
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4 dark:text-white">
              Tạo phòng ban mới
            </h3>
            <form onSubmit={handleCreateTeam}>
              <div className="mb-6">
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                  Tên phòng ban
                </label>
                <input
                  type="text"
                  value={newTeamName}
                  onChange={(e) => setNewTeamName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-600 dark:bg-gray-700 dark:text-white"
                  placeholder="Nhập tên phòng ban"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold mb-2">
                  Mô tả chung
                </label>
                <textarea
                  value={newTeamDescription}
                  onChange={(e) => setNewTeamDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-600 dark:bg-gray-700 dark:text-white"
                  placeholder="Nhập mô tả chung cho phòng ban (có thể để trống)"
                  rows={2}
                />
              </div>
              <div className="mb-6 flex items-center">
                <input
                  id="private-team-checkbox"
                  type="checkbox"
                  checked={newTeamPrivate}
                  onChange={(e) => setNewTeamPrivate(e.target.checked)}
                  className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="private-team-checkbox"
                  className="ml-2 text-sm text-gray-700 dark:text-gray-300"
                >
                  Chỉ mình tôi và thành viên được mời mới xem được phòng ban này
                  (Phòng ban riêng tư)
                </label>
              </div>
              <div className="mb-6">
                <div className="flex justify-between items-center mb-2">
                  <label className="block text-gray-700 dark:text-gray-300 text-sm font-bold">
                    Mời thành viên
                  </label>
                  <div className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                    <i className="fas fa-info-circle mr-1"></i>
                    <span>Bạn sẽ là Quản trị viên</span>
                  </div>
                </div>

                <div className="flex space-x-2 mb-2">
                  <div className="flex-1 relative">
                    <input
                      type="text"
                      value={memberEmail}
                      onChange={(e) => setMemberEmail(e.target.value)}
                      onFocus={() => {
                        if (searchResults.length > 0) {
                          setShowSearchResults(true);
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => setShowSearchResults(false), 200);
                      }}
                      className={`w-full px-3 py-2 border ${
                        emailError
                          ? "border-red-500"
                          : "border-gray-300 dark:border-gray-600"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-600 dark:bg-gray-700 dark:text-white`}
                      placeholder="Tìm kiếm theo tên hoặc email"
                    />
                    {emailError && (
                      <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                        {emailError}
                      </p>
                    )}

                    {showSearchResults && searchResults.length > 0 && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                        {searchResults.map((user) => (
                          <div
                            key={user.uid}
                            className="flex items-center p-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                            onClick={() => selectUserFromSearch(user)}
                          >
                            <div className="flex-shrink-0 h-10 w-10 rounded-full overflow-hidden">
                              <img
                                src={
                                  user.photoURL ||
                                  "https://cdn-icons-png.flaticon.com/512/6897/6897018.png"
                                }
                                alt={user.displayName}
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src =
                                    "https://cdn-icons-png.flaticon.com/512/6897/6897018.png";
                                }}
                              />
                            </div>
                            <div className="ml-3">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {user.displayName}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {user.email}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="w-48">
                    <select
                      value={memberRole}
                      onChange={(e) => setMemberRole(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-600 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="manager">Quản lý</option>
                      <option value="team_member">Thành viên phòng ban</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleAddMember()}
                    disabled={searchingUser}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                  >
                    {searchingUser ? (
                      <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                      "Thêm"
                    )}
                  </button>
                </div>
              </div>

              {teamMembers.length > 0 && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 dark:text-gray-300">
                    Thành viên đã thêm
                  </h4>
                  <div className="bg-gray-50 rounded-lg overflow-hidden dark:bg-gray-700">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                      <thead className="bg-gray-100 dark:bg-gray-600">
                        <tr>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                          >
                            Thành viên
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                          >
                            Vai trò
                          </th>
                          <th
                            scope="col"
                            className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-300"
                          >
                            Thao tác
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-600">
                        {teamMembers.map((member, index) => (
                          <tr key={index}>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-8 w-8 bg-emerald-100 rounded-full flex items-center justify-center dark:bg-emerald-900">
                                  <span className="text-emerald-800 font-medium dark:text-emerald-200">
                                    {member.displayName.charAt(0).toUpperCase()}
                                  </span>
                                </div>
                                <div className="ml-3">
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {member.displayName}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {member.email}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <select
                                value={member.role}
                                onChange={(e) =>
                                  changeMemberRole(member.email, e.target.value)
                                }
                                className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                              >
                                <option value="manager">Quản lý</option>
                                <option value="team_member">
                                  Thành viên phòng ban
                                </option>
                              </select>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                type="button"
                                onClick={() => removeMember(member.email)}
                                className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                aria-label="Xóa thành viên"
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewTeamName("");
                    setTeamMembers([]);
                    setEmailError("");
                  }}
                  className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-emerald-500 dark:bg-emerald-600 text-white rounded-lg hover:bg-emerald-600 dark:hover:bg-emerald-700"
                >
                  Tạo
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showTeamDetailModal && selectedTeam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold dark:text-gray-100">
                {selectedTeam.name}
              </h3>
              <button
                onClick={closeTeamDetails}
                className="bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 p-2 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 group relative"
                aria-label="Đóng chi tiết phòng ban"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-gray-600 dark:text-gray-300"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
                <span className="absolute -bottom-8 right-0 whitespace-nowrap bg-gray-800 text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  Đóng
                </span>
              </button>
            </div>

            <div className="mb-6">
              <h4 className="text-lg font-semibold mb-2 dark:text-gray-200">
                Thông tin phòng ban
              </h4>
              <div className="grid grid-cols-2 gap-4 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Tạo bởi
                  </p>
                  <p className="text-gray-800 dark:text-gray-200">
                    {selectedTeam.membersData?.find(
                      (m) => m.uid === selectedTeam.createdBy
                    )?.displayName || "Không xác định"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Tạo lúc
                  </p>
                  <p className="text-gray-800 dark:text-gray-200">
                    {(() => {
                      // Xử lý hiển thị thời gian theo nhiều định dạng có thể có từ Firestore
                      if (!selectedTeam.createdAt) {
                        return "Không xác định";
                      }
                      
                      try {
                        // Log định dạng timestamp để debug
                        console.log("Định dạng timestamp:", selectedTeam.createdAt);
                        
                        // Trường hợp 1a: createdAt là đối tượng Timestamp của Firestore với seconds và nanoseconds
                        if (selectedTeam.createdAt.seconds) {
                          return new Date(selectedTeam.createdAt.seconds * 1000).toLocaleString('vi-VN');
                        }
                        // Trường hợp 1b: createdAt là đối tượng Timestamp của Firestore với _seconds và _nanoseconds
                        else if (selectedTeam.createdAt._seconds) {
                          return new Date(selectedTeam.createdAt._seconds * 1000).toLocaleString('vi-VN');
                        }
                        // Trường hợp 2: createdAt là đối tượng Date của JavaScript
                        else if (selectedTeam.createdAt instanceof Date) {
                          return selectedTeam.createdAt.toLocaleString('vi-VN');
                        }
                        // Trường hợp 3: createdAt là timestamp dạng số
                        else if (typeof selectedTeam.createdAt === 'number') {
                          return new Date(selectedTeam.createdAt).toLocaleString('vi-VN');
                        }
                        // Trường hợp 4: createdAt là đối tượng với toDate() (Firebase v9+)
                        else if (typeof selectedTeam.createdAt.toDate === 'function') {
                          return selectedTeam.createdAt.toDate().toLocaleString('vi-VN');
                        }
                        // Các trường hợp khác
                        else {
                          console.log("Không thể xác định định dạng timestamp:", JSON.stringify(selectedTeam.createdAt));
                          return "Không xác định";
                        }
                      } catch (err) {
                        console.error("Lỗi khi định dạng thời gian:", err);
                        return "Không xác định";
                      }
                    })()}
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h4 className="text-lg font-semibold mb-2 dark:text-gray-200">
                Mô tả chung
              </h4>
              {isEditingDescription ? (
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <textarea
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-800 dark:text-white mb-2"
                    rows={3}
                    value={editedDescription}
                    onChange={e => setEditedDescription(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <button
                      className="px-4 py-1.5 bg-emerald-500 text-white rounded hover:bg-emerald-600 dark:bg-emerald-600 dark:hover:bg-emerald-700"
                      onClick={async () => {
                        try {
                          const updateTeamDescription = httpsCallable(functions, "updateTeamDescription");
                          await updateTeamDescription({ teamId: selectedTeam.id, description: editedDescription });
                          setSelectedTeam(prev => ({ ...prev, description: editedDescription }));
                          setTeams(teams => teams.map(t => t.id === selectedTeam.id ? { ...t, description: editedDescription } : t));
                          setIsEditingDescription(false);
                          setToast({ show: true, message: "Cập nhật mô tả thành công!", type: "success" });
                        } catch (err) {
                          setToast({ show: true, message: "Không thể cập nhật mô tả. Vui lòng thử lại.", type: "error" });
                        }
                      }}
                    >
                      Lưu
                    </button>
                    <button
                      className="px-4 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                      onClick={() => { setIsEditingDescription(false); setEditedDescription(selectedTeam.description || ""); }}
                    >
                      Hủy
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg text-gray-700 dark:text-gray-200 whitespace-pre-line">
                  {linkifyText(selectedTeam.description) || (
                    <span className="italic text-gray-400">Chưa có mô tả</span>
                  )}
                  {checkIsAdmin(selectedTeam) && (
                    <button
                      className="ml-4 px-3 py-1 text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 rounded hover:bg-emerald-200 dark:hover:bg-emerald-800 ml-2"
                      onClick={() => { setIsEditingDescription(true); setEditedDescription(selectedTeam.description || ""); }}
                    >
                      Sửa mô tả
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <h4 className="text-lg font-semibold dark:text-gray-200">
                  Thành viên phòng ban
                </h4>

                {checkIsManager(selectedTeam) && (
                  <div className="text-sm text-emerald-500 dark:text-emerald-400 hover:text-emerald-600 dark:hover:text-emerald-300 flex items-center">
                    <i className="fas fa-plus-circle mr-1"></i>
                    Thêm thành viên
                  </div>
                )}
              </div>

              {checkIsManager(selectedTeam) && (
                <div className="mb-4 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                  <div className="flex space-x-2">
                    <div className="flex-1 relative">
                      <input
                        type="text"
                        value={newMemberEmail}
                        onChange={(e) => setNewMemberEmail(e.target.value)}
                        onFocus={() => {
                          if (newMemberSearchResults.length > 0) {
                            setShowNewMemberSearchResults(true);
                          }
                        }}
                        onBlur={() => {
                          setTimeout(
                            () => setShowNewMemberSearchResults(false),
                            200
                          );
                        }}
                        className={`w-full px-3 py-2 border ${
                          newMemberError
                            ? "border-red-500"
                            : "border-gray-300 dark:border-gray-600"
                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-600 dark:text-gray-200`}
                        placeholder="Tìm kiếm theo tên hoặc email"
                      />
                      {newMemberError && (
                        <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                          {newMemberError}
                        </p>
                      )}

                      {showNewMemberSearchResults &&
                        newMemberSearchResults.length > 0 && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
                            {newMemberSearchResults.map((user) => (
                              <div
                                key={user.uid}
                                className="flex items-center p-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer"
                                onClick={() => selectNewMemberFromSearch(user)}
                              >
                                <div className="flex-shrink-0 h-10 w-10 rounded-full overflow-hidden">
                                  <img
                                    src={
                                      user.photoURL ||
                                      "https://cdn-icons-png.flaticon.com/512/6897/6897018.png"
                                    }
                                    alt={user.displayName}
                                    className="h-full w-full object-cover"
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src =
                                        "https://cdn-icons-png.flaticon.com/512/6897/6897018.png";
                                    }}
                                  />
                                </div>
                                <div className="ml-3">
                                  <p className="text-sm font-medium text-gray-900 dark:text-gray-200">
                                    {user.displayName}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {user.email}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                    </div>
                    <div className="w-48">
                      <select
                        value={newMemberRole}
                        onChange={(e) => setNewMemberRole(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-gray-600 dark:text-gray-200"
                      >
                        <option value="manager">Quản lý</option>
                        <option value="team_member">
                          Thành viên phòng ban
                        </option>
                      </select>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleAddNewMember()}
                      disabled={addingNewMember}
                      className="px-4 py-2 bg-emerald-500 dark:bg-emerald-600 text-white rounded-lg hover:bg-emerald-600 dark:hover:bg-emerald-700 transition-colors"
                    >
                      {addingNewMember ? (
                        <i className="fas fa-spinner fa-spin"></i>
                      ) : (
                        "Thêm"
                      )}
                    </button>
                  </div>
                </div>
              )}

              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                  <thead className="bg-gray-100 dark:bg-gray-600">
                    <tr>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                      >
                        Thành viên
                      </th>
                      <th
                        scope="col"
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                      >
                        Vai trò
                      </th>
                      {checkIsAdmin(selectedTeam) && (
                        <th
                          scope="col"
                          className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                        >
                          Thao tác
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                    {selectedTeam.membersData?.map((member, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-emerald-100 dark:bg-emerald-900 rounded-full flex items-center justify-center">
                              <span className="text-emerald-800 dark:text-emerald-200 font-medium">
                                {member.displayName?.charAt(0).toUpperCase() ||
                                  "U"}
                              </span>
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900 dark:text-gray-200">
                                {member.displayName}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400">
                                {member.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {checkIsAdmin(selectedTeam) &&
                          member.uid !== auth.currentUser?.uid ? (
                            <select
                              value={member.role}
                              onChange={(e) =>
                                updateTeamMemberRole(member.uid, e.target.value)
                              }
                              disabled={memberUpdating}
                              className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-emerald-500 dark:bg-gray-700 dark:text-gray-200"
                            >
                              <option value="manager">Quản lý</option>
                              <option value="team_member">
                                Thành viên phòng ban
                              </option>
                            </select>
                          ) : (
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                member.role === "admin"
                                  ? "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200"
                                  : member.role === "manager"
                                  ? "bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200"
                                  : "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200"
                              }`}
                            >
                              {member.role === "admin"
                                ? "Quản trị viên"
                                : member.role === "manager"
                                ? "Quản lý"
                                : "Thành viên phòng ban"}
                            </span>
                          )}
                        </td>
                        {checkIsAdmin(selectedTeam) && (
                          <td className="px-4 py-3 whitespace-nowrap ">
                            {member.uid !== auth.currentUser?.uid &&
                              member.role !== "admin" && (
                                <button
                                  onClick={() =>
                                    confirmRemoveMember(member.uid)
                                  }
                                  className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                                  title="Xóa thành viên"
                                >
                                  <i className="fas fa-trash-alt"></i>
                                </button>
                              )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
              <div className="flex justify-between items-center">
                <div>
                  <button
                    onClick={() => goToTeamTasks(selectedTeam.id)}
                    className="px-4 py-2 bg-blue-500 dark:bg-blue-600 text-white rounded-lg hover:bg-blue-600 dark:hover:bg-blue-700 transition-colors flex items-center"
                  >
                    <i className="fas fa-tasks mr-2"></i>
                    Xem công việc
                  </button>
                </div>

                {checkIsAdmin(selectedTeam) && (
                  <div>
                    <button
                      onClick={openDisbandConfirmation}
                      className="px-4 py-2 bg-red-500 dark:bg-red-600 text-white rounded-lg hover:bg-red-600 dark:hover:bg-red-700"
                    >
                      Giải tán phòng ban
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {confirmDisbandModal && selectedTeam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4 dark:text-gray-200">
              Xác nhận giải tán phòng ban
            </h3>
            <p className="mb-6 text-gray-600 dark:text-gray-300">
              Bạn có chắc chắn muốn giải tán phòng ban "{selectedTeam.name}"?
              Hành động này không thể hoàn tác và tất cả dữ liệu phòng ban sẽ bị
              xóa vĩnh viễn.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={closeDisbandConfirmation}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
                disabled={teamDisbanding}
              >
                Hủy
              </button>
              <button
                onClick={disbandTeam}
                className="px-4 py-2 bg-red-500 dark:bg-red-600 text-white rounded-lg hover:bg-red-600 dark:hover:bg-red-700 flex items-center"
                disabled={teamDisbanding}
              >
                {teamDisbanding ? (
                  <>
                    <i className="fas fa-spinner fa-spin mr-2"></i>
                    Đang giải tán...
                  </>
                ) : (
                  "Giải tán phòng ban"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {showRemoveConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4 dark:text-gray-200">
              Xóa thành viên phòng ban
            </h3>
            <p className="mb-6 text-gray-600 dark:text-gray-300">
              Bạn có chắc chắn muốn xóa thành viên này khỏi phòng ban? Họ sẽ mất
              tất cả quyền truy cập vào tài nguyên và công việc của phòng ban.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={closeRemoveConfirmation}
                className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100"
              >
                Hủy
              </button>
              <button
                onClick={removeTeamMember}
                className="px-4 py-2 bg-red-500 dark:bg-red-600 text-white rounded-lg hover:bg-red-600 dark:hover:bg-red-700"
              >
                Xóa thành viên
              </button>
            </div>
          </div>
        </div>
      )}

      {toast.show && (
        <div className={`fixed bottom-6 right-6 z-50 px-4 py-2 rounded shadow-lg text-white transition-all ${toast.type === 'success' ? 'bg-emerald-600' : 'bg-red-500'}`}>
          {toast.message}
        </div>
      )}

      {showTaskDetailsModal && selectedTaskForDetail && (
        <TaskDetailsModal
          task={selectedTaskForDetail}
          teams={teams}
          onClose={handleCloseTaskDetailsModal}
        />
      )}
    </div>
  );
};

export default Teams;
